import React from 'react';
import {createPortal} from 'react-dom';
import {CopyOutlined, SearchOutlined} from '@ant-design/icons';
import {Button, Input, List, message, Spin, Tooltip, Typography} from 'antd';
import {diagramAiApi} from '@/api/diagramAiApi';
import ArchimatePaletteMiniIcon, {
	type PaletteModelerLike,
} from '@components/diagram/ArchimatePaletteMiniIcon';
import {
	boardFindShortcutLabel,
	isBoardFindShortcut,
	isTypingInFormField,
} from '@/utils/diagram/boardFindShortcut';
import {
	buildCanvasSearchRowMetaMap,
	getCanvasSearchRowMeta,
	type CanvasSearchRowMeta,
} from '@/utils/diagram/buildCanvasElementLabels';

export type DiagramAiBoardSearchProps = {
	projectId: number;
	diagramId: number;
	viewIndex: number;
	initialJson: object | string | undefined;
	/** Для миниатюр как в палитре (path + цвет слоя) */
	getModeler?: () => PaletteModelerLike | null;
	focusCanvasElementById: (canvasId: string) => void;
};

const DiagramAiBoardSearch: React.FC<DiagramAiBoardSearchProps> = ({
	projectId,
	diagramId,
	viewIndex,
	initialJson,
	getModeler,
	focusCanvasElementById,
}) => {
	const [open, setOpen] = React.useState(false);
	const [query, setQuery] = React.useState('');
	const [loading, setLoading] = React.useState(false);
	const [identifiers, setIdentifiers] = React.useState<string[]>([]);
	const [error, setError] = React.useState<string | null>(null);

	const rowMetaMap = React.useMemo(
		() => buildCanvasSearchRowMetaMap(initialJson, viewIndex),
		[initialJson, viewIndex]
	);

	const runningRef = React.useRef(false);

	const findShortcutHint = React.useMemo(() => boardFindShortcutLabel(), []);

	React.useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (open && e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				setOpen(false);
				return;
			}
			if (!isBoardFindShortcut(e)) return;
			if (isTypingInFormField(e.target)) return;
			e.preventDefault();
			e.stopPropagation();
			setOpen(true);
		};
		window.addEventListener('keydown', onKeyDown, true);
		return () => window.removeEventListener('keydown', onKeyDown, true);
	}, [open]);

	const runSearch = React.useCallback(async () => {
		const q = query.trim();
		if (!q || runningRef.current) return;
		runningRef.current = true;
		setLoading(true);
		setError(null);
		try {
			const res = await diagramAiApi.search(projectId, diagramId, {query: q, viewIndex});
			if (res.error) {
				setError(res.error.message || `Ошибка (${res.status})`);
				return;
			}
			setIdentifiers(res.data?.identifiers ?? []);
		} finally {
			runningRef.current = false;
			setLoading(false);
		}
	}, [diagramId, projectId, query, viewIndex]);

	const overlay =
		open &&
		createPortal(
			<div
				role="presentation"
				style={{
					position: 'fixed',
					inset: 0,
					zIndex: 10050,
					background: 'rgba(0,0,0,0.22)',
					backdropFilter: 'blur(2px)',
				}}
				onMouseDown={() => setOpen(false)}
			>
				<div
					role="dialog"
					aria-label="Поиск на доске"
					style={{
						position: 'absolute',
						top: 56,
						left: '50%',
						transform: 'translateX(-50%)',
						width: 'min(560px, 92vw)',
						maxHeight: 'min(72vh, 640px)',
						display: 'flex',
						flexDirection: 'column',
						gap: 0,
						background: 'rgba(255,255,255,0.94)',
						borderRadius: 14,
						boxShadow: '0 18px 48px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
						overflow: 'hidden',
					}}
					onMouseDown={e => e.stopPropagation()}
				>
					<div style={{padding: '14px 16px 10px'}}>
						<Input
							autoFocus
							size="large"
							placeholder="Поиск элементов на диаграмме…"
							prefix={<SearchOutlined style={{color: 'rgba(0,0,0,0.35)'}} />}
							value={query}
							onChange={e => setQuery(e.target.value)}
							onPressEnter={() => void runSearch()}
							disabled={loading}
							style={{
								borderRadius: 10,
								fontSize: 16,
								background: 'rgba(245,245,247,0.95)',
								border: '1px solid rgba(0,0,0,0.08)',
							}}
						/>
						<Typography.Paragraph
							type="secondary"
							style={{margin: '8px 0 0', fontSize: 11}}
						>
							Enter — поиск. Esc — закрыть.
						</Typography.Paragraph>
					</div>
					<div
						style={{
							borderTop: '1px solid rgba(0,0,0,0.06)',
							minHeight: 48,
							flex: 1,
							overflow: 'auto',
						}}
					>
						{loading && identifiers.length > 0 ? (
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									padding: '8px 0',
									borderBottom: '1px solid rgba(0,0,0,0.06)',
								}}
							>
								<Spin size="small" />
							</div>
						) : null}
						{loading && identifiers.length === 0 ? (
							<div style={{display: 'flex', justifyContent: 'center', padding: 24}}>
								<Spin />
							</div>
						) : null}
						{error ? (
							<Typography.Text type="danger" style={{display: 'block', padding: 16}}>
								{error}
							</Typography.Text>
						) : null}
						{identifiers.length > 0 ? (
							<List
								size="small"
								dataSource={identifiers}
								renderItem={id => {
									const meta = getCanvasSearchRowMeta(rowMetaMap, id);
									return (
										<List.Item
											key={id}
											style={{cursor: 'pointer', padding: '10px 16px'}}
											onClick={() => {
												focusCanvasElementById(id);
												setOpen(false);
											}}
										>
											<List.Item.Meta
												title={
													<SearchResultTitle
														meta={meta}
														getModeler={getModeler}
													/>
												}
												description={
													<div
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: 6,
															maxWidth: '100%',
														}}
													>
														<Typography.Text
															type="secondary"
															ellipsis
															style={{
																flex: 1,
																fontSize: 11,
																marginBottom: 0,
															}}
														>
															{id}
														</Typography.Text>
														<Tooltip
															title="Скопировать"
															getPopupContainer={() => document.body}
															zIndex={10060}
														>
															<Button
																type="text"
																size="small"
																icon={<CopyOutlined />}
																aria-label="Скопировать идентификатор"
																style={{
																	flexShrink: 0,
																	color: 'rgba(0,0,0,0.45)',
																}}
																onClick={e => {
																	e.stopPropagation();
																	void navigator.clipboard
																		.writeText(id)
																		.then(() => {
																			void message.success(
																				'Скопировано'
																			);
																		});
																}}
															/>
														</Tooltip>
													</div>
												}
											/>
										</List.Item>
									);
								}}
							/>
						) : null}
						{!loading && !error && identifiers.length === 0 ? (
							<Typography.Text
								type="secondary"
								style={{display: 'block', padding: 16}}
							>
								{query.trim()
									? 'Ничего не найдено. Уточните запрос.'
									: 'Введите запрос и нажмите Enter.'}
							</Typography.Text>
						) : null}
					</div>
				</div>
			</div>,
			document.body
		);

	return (
		<>
			<Button
				type="text"
				icon={<SearchOutlined />}
				onClick={() => setOpen(true)}
				title={`Поиск на доске (${findShortcutHint})`}
				aria-label={`Поиск на доске, сочетание ${findShortcutHint}`}
				style={{flexShrink: 0}}
			/>
			{overlay}
		</>
	);
};

export default DiagramAiBoardSearch;

const MINI_ICON_SIZE = 20;

function SearchResultTitle({
	meta,
	getModeler,
}: {
	meta: CanvasSearchRowMeta;
	getModeler?: () => PaletteModelerLike | null;
}) {
	const modeler = getModeler?.() ?? null;
	return (
		<div style={{display: 'flex', alignItems: 'center', gap: 10, minWidth: 0}}>
			<ArchimatePaletteMiniIcon
				modeler={modeler}
				kind={meta.kind}
				archimateType={meta.typeLabel}
				size={MINI_ICON_SIZE}
				fallback={<FallbackSearchResultGlyph meta={meta} />}
			/>
			<Typography.Text ellipsis style={{flex: 1, marginBottom: 0}}>
				<strong>{meta.name}</strong>{' '}
				<span style={{color: 'rgba(0,0,0,0.45)', fontWeight: 400}}>({meta.typeLabel})</span>
			</Typography.Text>
		</div>
	);
}

function FallbackSearchResultGlyph({meta}: {meta: CanvasSearchRowMeta}) {
	const w = MINI_ICON_SIZE + 6;
	const h = Math.round(MINI_ICON_SIZE * 0.65);
	if (meta.shape === 'connector') {
		return (
			<span style={{display: 'inline-flex', alignItems: 'center', flexShrink: 0}} aria-hidden>
				<svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
					<line
						x1="2"
						y1={h / 2}
						x2={w - 7}
						y2={h / 2}
						stroke={meta.line}
						strokeWidth="1.5"
					/>
					<path
						d={`M ${w - 7} ${h / 2 - 3.5} L ${w - 1.5} ${h / 2} L ${w - 7} ${h / 2 + 3.5}`}
						stroke={meta.line}
						strokeWidth="1.5"
						fill="none"
					/>
				</svg>
			</span>
		);
	}
	if (meta.shape === 'circle') {
		const d = 14;
		return (
			<span
				style={{
					display: 'inline-block',
					flexShrink: 0,
					width: d,
					height: d,
					borderRadius: '50%',
					background: meta.fill,
					border: `1px solid ${meta.line}`,
					boxSizing: 'border-box',
				}}
				aria-hidden
			/>
		);
	}
	if (meta.shape === 'hex') {
		return (
			<span
				style={{
					display: 'inline-block',
					flexShrink: 0,
					width: 20,
					height: 14,
					background: meta.fill,
					border: `1px solid ${meta.line}`,
					clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
					boxSizing: 'border-box',
				}}
				aria-hidden
			/>
		);
	}
	const radius = meta.shape === 'roundRect' ? 5 : 2;
	return (
		<span
			style={{
				display: 'inline-block',
				flexShrink: 0,
				width: 22,
				height: 12,
				background: meta.fill,
				border: `1px solid ${meta.line}`,
				borderRadius: radius,
				boxSizing: 'border-box',
			}}
			aria-hidden
		/>
	);
}
